import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { NegotiationPaymentMethod } from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { IAgreementInstallment, SinisterAgreementService } from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";
import { PAYMENT_METHOD_OPTIONS } from "../../../creditRecovery/constants/paymentMethodOptions";

interface Props {
	open: boolean;
	agreementId: string;
	installments: IAgreementInstallment[];
	onClose: () => void;
	onConfirm: () => void;
}

const isEligible = (installment: IAgreementInstallment) => installment.status !== "paid" && installment.status !== "canceled";

export const ModalBulkManualSettleInstallments = ({ open, agreementId, installments, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [paymentMethod, setPaymentMethod] = useState<NegotiationPaymentMethod>("boleto");
	const [paymentDate, setPaymentDate] = useState("");
	const [note, setNote] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const eligible = installments.filter(isEligible);
	const skippedCount = installments.length - eligible.length;

	useEffect(() => {
		if (open) {
			setPaymentMethod("boleto");
			setPaymentDate(format(new Date(), "yyyy-MM-dd"));
			setNote("");
		}
	}, [open]);

	const handleConfirm = async () => {
		setIsSubmitting(true);
		const results = await Promise.all(
			eligible.map((installment) =>
				SinisterAgreementService.manualSettleInstallment(agreementId, installment.number, {
					paymentMethod,
					paymentDate: paymentDate || undefined,
					note: note || undefined,
				})
			)
		);
		setIsSubmitting(false);

		const failures = results.filter((result) => result instanceof Error) as Error[];
		const successCount = results.length - failures.length;

		if (successCount > 0) {
			toastHandler.present({
				type: failures.length > 0 ? "warning" : "success",
				messageOrContent:
					failures.length > 0
						? `${successCount} parcela(s) baixada(s). ${failures.length} falharam.`
						: `${successCount} parcela(s) baixada(s) manualmente.`,
				position: "top-right",
			});
		} else if (failures.length > 0) {
			toastHandler.present({ type: "error", messageOrContent: failures[0].message, position: "top-right" });
		}

		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Baixa Manual em Massa
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Alert severity="warning" sx={{ py: 0.5 }}>
							{eligible.length} parcela(s) selecionada(s) — total{" "}
							{formatScaleValueBR(String(eligible.reduce((sum, installment) => sum + installment.value, 0)))}. Isso irá marcar todas como pagas
							manualmente, fora do fluxo de pagamento padrão.
						</Alert>

						{skippedCount > 0 && (
							<Alert severity="info" sx={{ py: 0.5 }}>
								{skippedCount} parcela(s) selecionada(s) já paga(s) ou cancelada(s) serão ignoradas.
							</Alert>
						)}

						<TextField
							select
							fullWidth
							size="small"
							label="Forma de pagamento"
							value={paymentMethod}
							onChange={(e) => setPaymentMethod(e.target.value as NegotiationPaymentMethod)}
						>
							{PAYMENT_METHOD_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>

						<TextField
							type="date"
							fullWidth
							size="small"
							label="Data do pagamento"
							value={paymentDate}
							onChange={(e) => setPaymentDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>

						<TextField
							fullWidth
							size="small"
							multiline
							minRows={2}
							label="Observação (opcional)"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Aplicada a todas as parcelas selecionadas."
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Voltar</Button>
					<Box flex={1} />
					<Button variant="contained" disabled={isSubmitting || eligible.length === 0} onClick={handleConfirm}>
						Confirmar Baixa ({eligible.length})
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
